// packages/web-ui/src/AppShell/TabContent.tsx
//
// W2-S4 AC #10 — body of the active tab. Reads `selectActiveTab(state)` and
// switches to a per-destination stub. All six destinations are stubs in this
// story (real surfaces in W2-S5, W3-S2, W4-S1, W4-S5, W4-S7, W6-S2).
// ARIA: `<div role="tabpanel" id="panel-{tabId}" aria-labelledby="tab-{tabId}"
// tabIndex={-1}>`. Focus moves to the wrapper on every active-tab change.
//
// W2-S10b Task 2.4 (AC #2) — when no tabs are open, the wrapper hosts the
// `<EmptyWelcome>` component. Quick-action click navigates to the chat
// destination so users self-onboard without an open tab.
//
// W4 fix-pass HIGH-C / HIGH-D — replaces the `<ViewRendererStub>` and
// `<DiffPanelStub>` with the lazy-loaded real components shipped under W4-S2
// and W4-S7 respectively. The stub modules are kept on disk for one release
// in case other call sites still import them, but TabContent no longer
// renders them.

import { lazy, Suspense, useCallback, useEffect, useRef } from 'react';
import type { JSX } from 'react';
import { useStore } from 'zustand';
import { useRootStore } from '../state/store.js';
import {
  buildTabId,
  isNavSurfaceRoute,
  selectActiveTab,
  type ActiveDestination,
} from '../state/navigation.js';
import { ChatPanel } from '../panels/chat-panel/index.js';
import { FileViewer } from '../panels/file-viewer/index.js';
import { EmptyWelcome } from '../components/welcome/EmptyWelcome.js';
import type { QuickActionIntent } from '../components/welcome/QuickActions.js';
import { navigateTo } from '../router/hash-router.js';
import { sessionsStoreSingleton } from '../state/sessions-store-singleton.js';
import { selectReusableBlankSession } from '../state/sessions.js';

// W6-S2 — replace `<SettingsPanelStub>` with the lazily-loaded real panel
// so the `settings` chunk fits the 300 KB budget (Architecture §8.9).
const SettingsPanel = lazy(() => import('../panels/settings/index.js'));

// Skills pane — lazy-loaded so the chat-tab cold path stays light when the
// user never opens the Skills destination.
const SkillsPanel = lazy(() => import('../panels/skills/index.js'));

// `<KbEntityHost>` mounts `<ViewRenderer>` (ReactFlow + elkjs) inside its
// own lazy chunk so the chat-tab cold path stays light when the user
// never opens a kb tab.
const KbEntityHost = lazy(() =>
  import('../panels/kb-entity/index.js').then((m) => ({
    default: m.KbEntityHost,
  })),
);

// W4 fix-pass HIGH-C — `<ViewRendererPanel>` is lazy-loaded so the
// chat-tab cold path stays light. The `views-*.js` chunk gzips at ≈ 5 KB
// per the W4-S1 land record, well under the 1 MB ceiling.
const ViewRendererPanel = lazy(() =>
  import('../panels/view-renderer/index.js').then((m) => ({
    default: m.ViewRendererPanel,
  })),
);

// W4 fix-pass HIGH-D — `<DiffTab>` is lazy-loaded for the same reason as
// `<ViewRendererPanel>` above.
const DiffTab = lazy(() =>
  import('../panels/diff/index.js').then((m) => ({ default: m.DiffTab })),
);

const GitPanel = lazy(() =>
  import('../panels/git/index.js').then((m) => ({ default: m.GitPanel })),
);

/** The `{destination, resourceId}` route whose surface fills the main area. */
interface SurfaceRoute {
  destination: ActiveDestination;
  resourceId: string | null;
  /** Canonical tab id — used for the tabpanel `id`/`aria-labelledby`. */
  id: string;
  /** Whether a real tab in the strip backs this surface (document routes). */
  hasTab: boolean;
}

/**
 * Renders the body of the current route.
 *
 * The main content area is route-driven, not strictly tab-driven: a *document*
 * route (`chat`, or `kb`/`files`/`views`/`diff` with a resource) surfaces its
 * active tab, while a *navigation surface* (a bare landing, or
 * `settings`/`skills`/`git`) renders in the same area **without** a tab in the
 * strip. When neither is active — no open document and no nav surface — we host
 * the empty-tabs welcome.
 */
export function TabContent(): JSX.Element {
  const activeTab = useRootStore((s) => selectActiveTab(s));
  const activeDestination = useRootStore((s) => s.activeDestination);
  const activeResourceId = useRootStore((s) => s.activeResourceId);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const surface: SurfaceRoute | null =
    activeTab !== null
      ? {
          destination: activeTab.destination,
          resourceId: activeTab.resourceId,
          id: activeTab.id,
          hasTab: true,
        }
      : isNavSurfaceRoute(activeDestination, activeResourceId)
        ? {
            destination: activeDestination,
            resourceId: activeResourceId,
            id: buildTabId(activeDestination, activeResourceId),
            hasTab: false,
          }
        : null;

  // On every surface change, focus the wrapper (UX §18.2 + AC #10). Also runs
  // when a bulk close ("Close all") empties the tab list: focusing the
  // empty-welcome wrapper keeps keyboard focus from being orphaned on <body>
  // when the control that triggered the close unmounts. Skips focus-stealing
  // if an input is currently focused (W2-S3 pattern), and skips the initial
  // empty mount (only a transition *to* empty should grab focus).
  const hadSurface = useRef(false);
  useEffect(() => {
    const active = document.activeElement;
    const isEditing =
      active instanceof HTMLElement &&
      active.matches('input, textarea, [contenteditable="true"]');
    const transitioningToEmpty = surface === null && hadSurface.current;
    hadSurface.current = surface !== null;
    if (isEditing) return;
    if (surface !== null || transitioningToEmpty) {
      wrapperRef.current?.focus();
    }
  }, [surface?.id]);

  // Empty state: no document tab and no nav surface → host `<EmptyWelcome>`
  // (W2-S10b Task 2.4 / AC #2). The wrapper keeps its `empty-tabs-welcome`
  // test-id for back-compat with W2-S4's `<TabContent>` empty-state assertion.
  if (surface === null) {
    return (
      <div
        ref={wrapperRef}
        data-testid="empty-tabs-welcome"
        tabIndex={-1}
        className="h-full overflow-auto"
        style={{ transition: 'opacity 200ms' }}
      >
        <EmptyWelcomeHost />
      </div>
    );
  }

  return (
    <div
      ref={wrapperRef}
      role="tabpanel"
      id={`panel-${surface.id}`}
      // Only reference a strip tab when one actually backs this surface —
      // nav-surface routes have no `tab-<id>` element to label the panel.
      aria-labelledby={surface.hasTab ? `tab-${surface.id}` : undefined}
      tabIndex={-1}
      data-testid="tab-content-panel"
      data-active-tab={surface.hasTab ? surface.id : undefined}
      data-nav-surface={surface.hasTab ? undefined : surface.destination}
      className="flex-1 overflow-auto transition-opacity duration-200 motion-reduce:duration-100"
    >
      {renderSurface(surface.destination, surface.resourceId)}
    </div>
  );
}

/**
 * W2-S10b Task 2.4 — host wrapper for `<EmptyWelcome>`.
 *
 * The host owns the dispatch logic so the presentation component stays free
 * of zustand wiring (matches the W2-S10 design). Quick-action clicks navigate
 * to the chat destination; recent-row activations navigate to the matching
 * destination. Sessions/files rails default to soft-empty placeholders — the
 * sessions slice and the recent-files endpoint are not yet composed into the
 * root store, so we pass empty arrays and let `<RecentList>` show its
 * "Nothing here yet" muted line.
 */
function EmptyWelcomeHost(): JSX.Element {
  const onQuickAction = useCallback((intent: QuickActionIntent) => {
    // All three intents land the user in chat. The intent label is reserved
    // for future stories that pre-seed the prompt; we only need to navigate
    // here so the destination flips and the user can begin a session.
    void intent;
    navigateTo({ kind: 'chat' });
  }, []);

  const onActivateSession = useCallback((sessionId: string) => {
    navigateTo({ kind: 'chat', sessionId });
  }, []);

  const onActivateFile = useCallback((path: string) => {
    navigateTo({ kind: 'files', path });
  }, []);

  return (
    <EmptyWelcome
      recentSessions={[]}
      recentFiles={[]}
      recentFilesStatus="idle"
      onQuickAction={onQuickAction}
      onActivateSession={onActivateSession}
      onActivateFile={onActivateFile}
    />
  );
}

/**
 * Auto-select a chat session whenever the chat tab opens with no
 * sessionId in the URL. Behaviour:
 *   - status `idle` → fire `refreshSessions()` once.
 *   - status `ready` → open a *fresh* conversation. Reuse an existing blank
 *     (0-message, untitled) session if one is already on disk; otherwise POST
 *     a new one. This matches the "Chat opens a new chat" behaviour of the
 *     activity-bar button/Ctrl+1 without resurrecting a real conversation —
 *     while avoiding the empty-session leak that came from unconditionally
 *     creating a throwaway session on every bare-`/chat` landing.
 *   - status `error` → render a retry button (otherwise the user would
 *     be stuck on a silent placeholder when the server is down).
 *
 * The selector replaces the placeholder "chat" tab with the resolved
 * session-scoped tab in a single tick (`openTab` followed by
 * `closeTab('chat')`) so the user never sees a transient duplicate
 * tab in the bar.
 */
function ChatAutoSelector(): JSX.Element {
  const state = useStore(sessionsStoreSingleton);
  const triggered = useRef(false);

  useEffect(() => {
    if (state.status === 'idle') {
      void sessionsStoreSingleton.getState().refreshSessions();
    }
  }, [state.status]);

  useEffect(() => {
    if (triggered.current) return;
    if (state.status !== 'ready') return;
    if (state.createInFlight) return;
    triggered.current = true;
    void (async () => {
      // Reuse an existing not-yet-used (0-message, untitled) session instead of
      // minting a fresh one on every bare-`/chat` landing. Without this, each
      // app boot / reload / Chat-button click that resolves to `/chat`
      // persisted a brand-new empty session to disk — and nothing prunes them
      // except an explicit tab close — so the list filled up with permanent
      // "Untitled / 0 messages" rows. Reusing bounds the blanks to at most one
      // at a time (the newest, since `sessions` is sorted updatedAt-desc).
      const store = sessionsStoreSingleton.getState();
      const blank = selectReusableBlankSession(store.sessions);
      const id = blank !== null ? blank.sessionId : await store.createSession();
      if (id !== null) {
        replaceEmptyChatTab(id);
      } else {
        triggered.current = false;
      }
    })();
  }, [state.status, state.createInFlight]);

  return (
    <div
      data-testid="chat-auto-selector"
      className="p-4 text-sm text-taupe-700"
    >
      {state.status === 'error' ? (
        <div className="space-y-2">
          <p>Could not load chat sessions.</p>
          <button
            type="button"
            className="rounded border border-taupe-400 bg-cream-100 px-2 py-1 text-xs"
            onClick={() => {
              triggered.current = false;
              void sessionsStoreSingleton.getState().refreshSessions();
            }}
          >
            Retry
          </button>
        </div>
      ) : (
        <p>Opening chat session…</p>
      )}
    </div>
  );
}

/**
 * Atomically swap the placeholder `chat` tab for the resolved
 * `chat/<sessionId>` tab. We dispatch `openTab` (activates the real
 * session) and `closeTab('chat')` against the root store in the same
 * tick so the tab bar never shows both at once. `navigateTo` would
 * also work but routes through an async hashchange — the user would
 * briefly see two chat tabs.
 */
function replaceEmptyChatTab(sessionId: string): void {
  const { openTab, closeTab } = useRootStore.getState();
  openTab({
    destination: 'chat',
    resourceId: sessionId,
    label: 'New conversation',
    activate: true,
  });
  if (useRootStore.getState().openTabs.some((t) => t.id === 'chat')) {
    closeTab('chat');
  }
}

function renderSurface(
  destination: ActiveDestination,
  resourceId: string | null,
): JSX.Element | null {
  switch (destination) {
    case 'chat':
      // W2-S5 — real `<ChatPanel>` replaces the stub. When sessionId is
      // null (route /chat without an id), the auto-selector picks the
      // most-recent session or creates a new one so the chat tab is
      // never left empty.
      return resourceId !== null ? (
        <ChatPanel sessionId={resourceId} />
      ) : (
        <ChatAutoSelector />
      );
    case 'kb':
      return (
        <Suspense
          fallback={
            <div className="p-4 space-y-3" data-testid="kb-host-suspense">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-12 bg-black/5 rounded animate-pulse" />
              ))}
            </div>
          }
        >
          <KbEntityHost entityId={resourceId} />
        </Suspense>
      );
    case 'files':
      return <FileViewer path={resourceId} />;
    case 'views':
      // W4 fix-pass HIGH-C — mount the real `<ViewRendererPanel>` (W4-S2)
      // when a viewId is present. Defensive guard for null `resourceId`
      // (route /views without an id) so the user gets a friendly empty
      // state instead of a runtime error from the panel's `viewId`
      // assertion.
      return resourceId !== null ? (
        <Suspense
          fallback={
            <div
              className="p-4 space-y-3"
              data-testid="view-renderer-suspense"
            >
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-12 bg-black/5 rounded animate-pulse" />
              ))}
            </div>
          }
        >
          <ViewRendererPanel viewId={resourceId} />
        </Suspense>
      ) : (
        <div data-testid="views-no-resource" className="p-4 text-sm text-taupe-700">
          No view selected.
        </div>
      );
    case 'diff':
      // W4 fix-pass HIGH-D — mount the real `<DiffTab>` (W4-S7) when a
      // changesetId is present. Defensive guard mirrors the views branch.
      return resourceId !== null ? (
        <Suspense
          fallback={
            <div
              className="p-4 space-y-3"
              data-testid="diff-tab-suspense"
            >
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-12 bg-black/5 rounded animate-pulse" />
              ))}
            </div>
          }
        >
          <DiffTab changesetId={resourceId} />
        </Suspense>
      ) : (
        <div data-testid="diff-no-resource" className="p-4 text-sm text-taupe-700">
          No changeset selected.
        </div>
      );
    case 'git':
      return (
        <Suspense
          fallback={
            <div className="p-4 space-y-3" data-testid="git-panel-suspense">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-12 bg-black/5 rounded animate-pulse" />
              ))}
            </div>
          }
        >
          <GitPanel />
        </Suspense>
      );
    case 'settings':
      return (
        <Suspense
          fallback={
            <div className="p-4 space-y-3" data-testid="settings-suspense">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-12 bg-black/5 rounded animate-pulse" />
              ))}
            </div>
          }
        >
          <SettingsPanel />
        </Suspense>
      );
    case 'skills':
      return (
        <Suspense
          fallback={
            <div className="p-4 space-y-3" data-testid="skills-suspense">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-12 bg-black/5 rounded animate-pulse" />
              ))}
            </div>
          }
        >
          <SkillsPanel selectedSkillName={resourceId} />
        </Suspense>
      );
  }
}
